/**
 * Shared helpers for the Google Calendar OAuth flow.
 *
 * iOS WebKit (all iOS browsers, including Safari) unreliably blocks the
 * "open an about:blank popup, then navigate it after an awaited fetch" pattern:
 * the popup opens blank and never navigates to Google, so the connect flow gets
 * stuck on "Connecting…" and no auth screen appears. For those devices we use a
 * same-tab full-page redirect instead. Impersonation survives the redirect
 * because the impersonation token lives in `localStorage`.
 */

/** sessionStorage key holding the in-app path to return to after a same-tab OAuth redirect. */
export const CALENDAR_OAUTH_RETURN_PATH = 'calendar_oauth_return_path';

/** sessionStorage key holding the OAuth result handed back from the callback page. */
export const CALENDAR_OAUTH_RESULT = 'calendar_oauth_result';

export type CalendarOAuthResult =
  | { type: 'OAUTH_SUCCESS'; code: string; state: string }
  | { type: 'OAUTH_ERROR'; error: string };

/**
 * True on iOS WebKit, where the popup-based OAuth flow is unreliable and a
 * same-tab redirect should be used instead.
 */
export function prefersSameTabOAuth(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ reports as desktop Safari but is still WebKit-on-touch.
    (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document);
  return isIOS;
}

/**
 * True when a same-tab OAuth redirect has returned a result that hasn't been
 * consumed yet. Host pages use this to auto-open the calendar modal so the
 * connection can be completed after the redirect.
 */
export function hasPendingCalendarOAuthResult(): boolean {
  try {
    return !!sessionStorage.getItem(CALENDAR_OAUTH_RESULT);
  } catch {
    return false;
  }
}
